import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Form,
  InputGroup,
  Button,
  Spinner,
  ListGroup,
  Card
} from 'react-bootstrap';
import { searchGeneralProducts } from '../api/aliApi';

const HeroSection = () => {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // refs
  const wrapRef = useRef(null);
  const inputWrapRef = useRef(null);
  const abortRef = useRef(null);

  // dropdown portal positioning
  const [anchorRect, setAnchorRect] = useState(null);

  const measure = useCallback(() => {
    if (!inputWrapRef.current) return;
    const rect = inputWrapRef.current.getBoundingClientRect();
    setAnchorRect({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width
    });
  }, []);

  useLayoutEffect(() => {
    if (showDropdown) {
      measure();
      const onScroll = () => measure();
      const onResize = () => measure();
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onResize);
      return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('resize', onResize);
      };
    }
  }, [showDropdown, measure]);

  // click outside
  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    async (q) => {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const res = await searchGeneralProducts(q, '', 1, { signal: controller.signal });
        const data =
          res?.aliexpress_ds_text_search_response?.data?.products?.selection_search_product || [];
        setSuggestions(data.slice(0, 6));
        setShowDropdown(true);
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('Search suggestion error:', err);
          setSuggestions([]);
          setShowDropdown(false);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // debounce search
  useEffect(() => {
    if (query.length < 2) {
      if (abortRef.current) abortRef.current.abort();
      setSuggestions([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }
    // show spinner immediately while typing
    setLoading(true);
    const t = setTimeout(() => fetchSuggestions(query), 280);
    return () => clearTimeout(t);
  }, [query, fetchSuggestions]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setShowDropdown(false);
    navigate(`/aliproduct?search=${encodeURIComponent(q)}`);
  };

  const Gear = ({ size = 44, opacity = 0.25 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={`rgba(255,255,255,${opacity})`} strokeWidth="1.5"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3.5" />
      <path d="M19.4 15a7.97 7.97 0 0 0 .1-6l-2.1.8-1-1.9 1.7-1.5a8.05 8.05 0 0 0-4.4-1.8l-.2 2.2h-2L9.8 4.6a8.05 8.05 0 0 0-4.4 1.8L7 7.9l-1 1.9-2.1-.8a7.97 7.97 0 0 0 .1 6l2.1-.8 1 1.9-1.7 1.5a8.05 8.05 0 0 0 4.4 1.8l.2-2.2h2l.2 2.2a8.05 8.05 0 0 0 4.4-1.8L16.9 16l1-1.9 1.5.9" />
    </svg>
  );

  const Wrench = ({ size = 42, opacity = 0.22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={`rgba(255,255,255,${opacity})`} strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a4.5 4.5 0 0 0 5 5l-7.8 7.8a2.5 2.5 0 1 1-3.5-3.5L16.2 7a4.5 4.5 0 0 0-1.5-.7Z" />
      <circle cx="8" cy="20" r="0.8" />
    </svg>
  );

  return (
    <section
      className="py-5"
      style={{
        position: 'relative',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '78vh',
        display: 'flex',
        alignItems: 'center',
        // allow dropdown to escape
        overflow: 'visible'
      }}
    >
      <style>{`
        @keyframes floatUp { 0%{transform:translateY(0) scale(1);opacity:.8}50%{transform:translateY(-18px) scale(1.02);opacity:1}100%{transform:translateY(0) scale(1);opacity:.8} }
        @keyframes floatUpSlow { 0%{transform:translateY(0) scale(1)}50%{transform:translateY(-12px) scale(1.01)}100%{transform:translateY(0) scale(1)} }
        @keyframes floatUpFast { 0%{transform:translateY(0) scale(1)}50%{transform:translateY(-24px) scale(1.03)}100%{transform:translateY(0) scale(1)} }
        @keyframes drift { 0%{transform:translate(0,0) rotate(0)}50%{transform:translate(8px,-10px) rotate(6deg)}100%{transform:translate(0,0) rotate(0)} }
        @keyframes bounceArrow { 0%,100%{transform:translateY(0)}50%{transform:translateY(6px)} }
        .bubble{position:absolute;border-radius:999px;background:radial-gradient(120px circle at 30% 30%, rgba(255,255,255,.25), rgba(255,255,255,.06) 60%, transparent 70%);border:1px solid rgba(255,255,255,.25);filter:blur(.3px);mix-blend-mode:screen}
        .bubble--1{width:140px;height:140px;left:6%;top:18%;animation:floatUp 5.5s ease-in-out .2s infinite}
        .bubble--2{width:110px;height:110px;left:20%;bottom:14%;animation:floatUpSlow 6.5s ease-in-out .7s infinite}
        .bubble--3{width:170px;height:170px;right:12%;top:16%;animation:floatUp 6s ease-in-out .4s infinite}
        .bubble--4{width:95px;height:95px;right:8%;bottom:12%;animation:floatUpSlow 7s ease-in-out .9s infinite}
        .bubble--5{width:120px;height:120px;left:38%;top:12%;animation:floatUpFast 5.2s ease-in-out .3s infinite}
        .bubble--6{width:80px;height:80px;left:32%;bottom:18%;animation:floatUp 6.8s ease-in-out .5s infinite}
        .bubble--7{width:150px;height:150px;right:28%;top:24%;animation:floatUpSlow 7.8s ease-in-out .2s infinite}
        .bubble--8{width:70px;height:70px;right:24%;bottom:22%;animation:floatUpFast 5.6s ease-in-out .6s infinite}
        .bubble--9{width:105px;height:105px;left:8%;bottom:30%;animation:floatUp 6.2s ease-in-out .4s infinite}
        .bubble--10{width:130px;height:130px;right:6%;top:36%;animation:floatUpSlow 7.4s ease-in-out .8s infinite}
        .part{position:absolute;animation:drift 7s ease-in-out infinite;filter:drop-shadow(0 4px 10px rgba(0,0,0,.15));mix-blend-mode:screen}
        .part--gear-1{left:12%;top:28%;animation-delay:.3s}
        .part--gear-2{right:18%;top:22%;animation-delay:.6s}
        .part--wrench-1{left:22%;bottom:18%;animation-delay:.9s}
        .part--wrench-2{right:10%;bottom:20%;animation-delay:.5s}
        .arrow-bounce{display:inline-block;animation:bounceArrow 1.2s ease-in-out infinite}

        /* portal dropdown */
        .dropdown-portal {
          position: absolute;
          z-index: 2147483000; /* above anything */
        }
      `}</style>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(var(--bs-primary-rgb), .94) 0%, rgba(var(--bs-primary-rgb), .9) 55%, rgba(var(--bs-primary-rgb), .88) 100%)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)'
        }}
      />

      {/* bubbles & parts */}
      <div className="bubble bubble--1" />
      <div className="bubble bubble--2" />
      <div className="bubble bubble--3" />
      <div className="bubble bubble--4" />
      <div className="bubble bubble--5" />
      <div className="bubble bubble--6" />
      <div className="bubble bubble--7" />
      <div className="bubble bubble--8" />
      <div className="bubble bubble--9" />
      <div className="bubble bubble--10" />
      <div className="part part--gear-1"><Gear /></div>
      <div className="part part--gear-2"><Gear size={50} opacity={0.22} /></div>
      <div className="part part--wrench-1"><Wrench /></div>
      <div className="part part--wrench-2"><Wrench size={48} opacity={0.2} /></div>

      <Container style={{ position: 'relative' }}>
        <Row className="justify-content-center text-center">
          <Col lg={9} xl={8}>
            <h1 className="display-6 fw-bold text-white mb-2">
              Discover quality auto parts worldwide — start your search today{' '}
              <span className="arrow-bounce" aria-hidden="true">↓</span>
            </h1>

            <div ref={wrapRef} className="position-relative mx-auto" style={{ maxWidth: 720 }}>
              <Form onSubmit={handleSearch} role="search" aria-label="Product search">
                <div ref={inputWrapRef}>
                  <InputGroup
                    className="shadow-sm rounded-4 overflow-hidden"
                    style={{
                      background: '#ffffff',
                      border: '1px solid rgba(var(--bs-primary-rgb), .28)',
                      boxShadow: '0 10px 28px rgba(0,0,0,.14)'
                    }}
                  >
                    <Form.Control
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
                      placeholder="Search products..."
                      className="py-3 border-0"
                      aria-label="Search products"
                      aria-expanded={showDropdown}
                      autoComplete="off"
                      style={{ background: '#fff', color: '#0b0b0b' }}
                    />
                    <Button
                      type="submit"
                      variant="primary"
                      className="px-4 fw-semibold"
                      style={{ transition: 'transform .15s ease', cursor: 'pointer' }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.03)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      Search
                    </Button>
                  </InputGroup>
                </div>
              </Form>

              {/* inline spinner while typing/fetching */}
              {(loading && query.length >= 2) && (
                <div
                  className="position-absolute"
                  style={{
                    right: 16,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    zIndex: 3
                  }}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <Spinner animation="border" size="sm" variant="primary" />
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      {/* Portal dropdown */}
      {showDropdown && anchorRect && ReactDOM.createPortal(
        <div
          className="dropdown-portal"
          style={{ top: anchorRect.top + 8, left: anchorRect.left, width: anchorRect.width }}
        >
          <Card
            className="shadow-sm border-0 rounded-4"
            style={{
              maxHeight: 380,
              overflowY: 'auto',
              background: 'rgba(255,255,255,.98)',
              border: '1px solid rgba(0,0,0,.06)'
            }}
            role="listbox"
          >
            {(!suggestions.length && !loading) ? (
              <Card.Body className="text-muted text-center py-3">No results found</Card.Body>
            ) : (
              <ListGroup variant="flush">
                {suggestions.map((product, idx) => (
                  <ListGroup.Item
                    key={idx}
                    action
                    className="d-flex align-items-center gap-3"
                    onClick={() => {
                      setShowDropdown(false);
                      navigate(`/aliproduct/${product.itemId}`);
                    }}
                    style={{ transition: 'background-color .15s ease', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(var(--bs-primary-rgb), .06)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                  >
                    <img
                      src={product.itemMainPic || 'https://placehold.co/56x56'}
                      alt=""
                      width={56}
                      height={56}
                      style={{ objectFit: 'cover', borderRadius: 8 }}
                    />
                    <div className="small text-truncate" style={{ maxWidth: '80%' }}>
                      {product.title}
                    </div>
                  </ListGroup.Item>
                ))}
                <ListGroup.Item
                  action
                  className="text-center fw-semibold"
                  style={{ color: 'var(--bs-primary)', cursor: 'pointer' }}
                  onClick={() => {
                    setShowDropdown(false);
                    navigate(`/aliproduct?search=${encodeURIComponent(query)}`);
                  }}
                >
                  View more results for “{query}”
                </ListGroup.Item>
              </ListGroup>
            )}
          </Card>
        </div>,
        document.body
      )}
    </section>
  );
};

export default HeroSection;
